import _objectSpread from "@babel/runtime/helpers/esm/objectSpread";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";

/**
 * Module constants
 */
var HEIGHT_OFFSET = 10; // used by the arrow and a bit of empty space

var isMobileViewport = function isMobileViewport() {
  return window.innerWidth < 782;
};

var isRTL = function isRTL() {
  return document.documentElement.dir === 'rtl';
};
/**
 * Utility used to compute the popover position over the xAxis
 *
 * @param {Object} anchorRect       Anchor Rect.
 * @param {Object} contentSize      Content Size.
 * @param {string} xAxis            Desired xAxis.
 * @param {string} chosenYAxis      yAxis to be used.
 *
 * @return {Object} Popover xAxis position and constraints.
 */


export function computePopoverXAxisPosition(anchorRect, contentSize, xAxis, chosenYAxis) {
  var width = contentSize.width; // Correct xAxis for RTL support

  if (xAxis === 'left' && isRTL()) {
    xAxis = 'right';
  } else if (xAxis === 'right' && isRTL()) {
    xAxis = 'left';
  } // x axis alignment choices


  var anchorMidPoint = Math.round(anchorRect.left + anchorRect.width / 2);
  var centerAlignment = {
    popoverLeft: anchorMidPoint,
    contentWidth: (anchorMidPoint - width / 2 > 0 ? width / 2 : anchorMidPoint) + (anchorMidPoint + width / 2 > window.innerWidth ? window.innerWidth - anchorMidPoint : width / 2)
  };
  var leftAlignmentX = chosenYAxis === 'middle' ? anchorRect.left : anchorMidPoint;
  var leftAlignment = {
    popoverLeft: leftAlignmentX,
    contentWidth: leftAlignmentX - width > 0 ? width : leftAlignmentX
  };
  var rightAlignmentX = chosenYAxis === 'middle' ? anchorRect.right : anchorMidPoint;
  var rightAlignment = {
    popoverLeft: rightAlignmentX,
    contentWidth: rightAlignmentX + width > window.innerWidth ? window.innerWidth - rightAlignmentX : width
  }; // Choosing the x axis

  var chosenXAxis;
  var contentWidth = null;

  if (xAxis === 'center' && centerAlignment.contentWidth === width) {
    chosenXAxis = 'center';
  } else if (xAxis === 'left' && leftAlignment.contentWidth === width) {
    chosenXAxis = 'left';
  } else if (xAxis === 'right' && rightAlignment.contentWidth === width) {
    chosenXAxis = 'right';
  } else {
    chosenXAxis = leftAlignment.contentWidth > rightAlignment.contentWidth ? 'left' : 'right';
    var chosenWidth = chosenXAxis === 'left' ? leftAlignment.contentWidth : rightAlignment.contentWidth;
    contentWidth = chosenWidth !== width ? chosenWidth : null;
  }

  var popoverLeft;

  if (chosenXAxis === 'center') {
    popoverLeft = centerAlignment.popoverLeft;
  } else if (chosenXAxis === 'left') {
    popoverLeft = leftAlignment.popoverLeft;
  } else {
    popoverLeft = rightAlignment.popoverLeft;
  }

  return {
    xAxis: chosenXAxis,
    popoverLeft: popoverLeft,
    contentWidth: contentWidth
  };
}
/**
 * Utility used to compute the popover position over the yAxis
 *
 * @param {Object} anchorRect       Anchor Rect.
 * @param {Object} contentSize      Content Size.
 * @param {string} yAxis            Desired yAxis.
 *
 * @return {Object} Popover xAxis position and constraints.
 */

export function computePopoverYAxisPosition(anchorRect, contentSize, yAxis) {
  var height = contentSize.height; // y axis alignment choices

  var anchorMidPoint = anchorRect.top + anchorRect.height / 2;
  var middleAlignment = {
    popoverTop: anchorMidPoint,
    contentHeight: (anchorMidPoint - height / 2 > 0 ? height / 2 : anchorMidPoint) + (anchorMidPoint + height / 2 > window.innerHeight ? window.innerHeight - anchorMidPoint : height / 2)
  };
  var topAlignment = {
    popoverTop: anchorRect.top,
    contentHeight: anchorRect.top - HEIGHT_OFFSET - height > 0 ? height : anchorRect.top - HEIGHT_OFFSET
  };
  var bottomAlignment = {
    popoverTop: anchorRect.bottom,
    contentHeight: anchorRect.bottom + HEIGHT_OFFSET + height > window.innerHeight ? window.innerHeight - HEIGHT_OFFSET - anchorRect.bottom : height
  }; // Choosing the y axis

  var chosenYAxis;
  var contentHeight = null;

  if (yAxis === 'middle' && middleAlignment.contentHeight === height) {
    chosenYAxis = 'middle';
  } else if (yAxis === 'top' && topAlignment.contentHeight === height) {
    chosenYAxis = 'top';
  } else if (yAxis === 'bottom' && bottomAlignment.contentHeight === height) {
    chosenYAxis = 'bottom';
  } else {
    chosenYAxis = topAlignment.contentHeight > bottomAlignment.contentHeight ? 'top' : 'bottom';
    var chosenHeight = chosenYAxis === 'top' ? topAlignment.contentHeight : bottomAlignment.contentHeight;
    contentHeight = chosenHeight !== height ? chosenHeight : null;
  }

  var popoverTop;

  if (chosenYAxis === 'middle') {
    popoverTop = middleAlignment.popoverTop;
  } else if (chosenYAxis === 'top') {
    popoverTop = topAlignment.popoverTop;
  } else {
    popoverTop = bottomAlignment.popoverTop;
  }

  return {
    yAxis: chosenYAxis,
    popoverTop: popoverTop,
    contentHeight: contentHeight
  };
}
/**
 * Utility used to compute the popover position and the content max width/height for a popover
 * given its anchor rect and its content size.
 *
 * @param {Object} anchorRect       Anchor Rect.
 * @param {Object} contentSize      Content Size.
 * @param {string} position         Position.
 * @param {boolean} expandOnMobile  Whether to expand the popover on mobile or not.
 *
 * @return {Object} Popover position and constraints.
 */

export function computePopoverPosition(anchorRect, contentSize) {
  var position = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'top';
  var expandOnMobile = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;

  var _position$split = position.split(' '),
      _position$split2 = _slicedToArray(_position$split, 2),
      yAxis = _position$split2[0],
      _position$split2$ = _position$split2[1],
      xAxis = _position$split2$ === void 0 ? 'center' : _position$split2$;

  var yAxisPosition = computePopoverYAxisPosition(anchorRect, contentSize, yAxis);
  var xAxisPosition = computePopoverXAxisPosition(anchorRect, contentSize, xAxis, yAxisPosition.yAxis);
  return _objectSpread({
    isMobile: isMobileViewport() && expandOnMobile
  }, xAxisPosition, yAxisPosition);
}
//# sourceMappingURL=utils.js.map